/**
 * Ритм платежа: одно правило на все формы приложения.
 *
 * Периодичность спрашивают в трёх местах — настройка, добавление платежа,
 * правка платежа, — и списки в них успели разойтись: в одном была «раз в две
 * недели», в другом нет, а разового платежа не было нигде. Человек с налогом
 * к 1 декабря заводил его ежемесячным, и приложение вычитало налог каждый
 * месяц.
 *
 * Поэтому и набор вариантов, и перевод в расписание, и обратное чтение живут
 * здесь. Форма только показывает — расходиться ей больше не на чем.
 */
import { onceOn, scheduleLabel, type PaymentSchedule } from './schedule.js';
import type { PlainDate, ShiftRule } from './date.js';

/** Что человек выбирает в поле «Повторение». */
export type RepeatKey =
  | 'never' | 'day' | 'week' | 'twoWeeks' | 'month' | 'quarter' | 'halfYear' | 'year';

export interface RepeatOption {
  key: RepeatKey;
  label: string;
  /** Шаг повторения. У разового его нет. */
  every?: { n: number; unit: 'day' | 'week' | 'month' | 'year' };
}

/**
 * Список вариантов в порядке показа.
 *
 * «Никогда» стоит первым, но **умолчанием не является**: обязательный платёж
 * по природе повторяющийся, и разовый по умолчанию означал бы, что аренда,
 * заведённая в спешке, исчезнет из расчёта со следующего месяца, а «Можно
 * потратить» вырастет на её сумму. Узнают об этом в конце месяца, когда
 * деньги уже потрачены.
 */
export const REPEAT_OPTIONS: RepeatOption[] = [
  { key: 'never', label: 'Никогда' },
  { key: 'day', label: 'Каждый день', every: { n: 1, unit: 'day' } },
  { key: 'week', label: 'Каждую неделю', every: { n: 1, unit: 'week' } },
  { key: 'twoWeeks', label: 'Раз в две недели', every: { n: 2, unit: 'week' } },
  { key: 'month', label: 'Каждый месяц', every: { n: 1, unit: 'month' } },
  { key: 'quarter', label: 'Раз в квартал', every: { n: 3, unit: 'month' } },
  { key: 'halfYear', label: 'Раз в полгода', every: { n: 6, unit: 'month' } },
  { key: 'year', label: 'Каждый год', every: { n: 1, unit: 'year' } },
];

/** Умолчание: платёж, который заводят, обычно повторяется каждый месяц. */
export const DEFAULT_REPEAT: RepeatKey = 'month';

/** Чем заканчивается повторение. */
export type RepeatEnd =
  | { kind: 'never' }
  /** Последний платёж не позже этой даты. */
  | { kind: 'date'; date: PlainDate }
  /** Столько платежей, считая первый. */
  | { kind: 'times'; times: number };

export const NEVER_ENDS: RepeatEnd = { kind: 'never' };

/**
 * Расписание из ответов формы.
 *
 * `anchor` — дата платежа. Для повторения она задаёт и день, и фазу: платёж
 * 31-го числа в феврале переносится на 28-е, а в марте снова наступает 31-го.
 *
 * `from` — с какой даты действует расписание. Ставится при **изменении**
 * ритма: новый ритм не должен задним числом выдумывать платежи, которых не
 * было. Уже подтверждённые оплаты при этом остаются как есть.
 */
export function scheduleFrom(input: {
  anchor: PlainDate;
  repeat: RepeatKey;
  end?: RepeatEnd;
  from?: PlainDate;
  shiftRule?: ShiftRule;
}): PaymentSchedule {
  const shiftRule = input.shiftRule ?? 'exact';
  const option = REPEAT_OPTIONS.find((o) => o.key === input.repeat);

  // Разовый платёж не кончается — он случается один раз. Поле окончания для
  // него не показывается и в расписание не попадает.
  if (!option?.every) return onceOn(input.anchor, shiftRule);

  const end = input.end ?? NEVER_ENDS;
  return {
    kind: 'every',
    anchor: input.anchor,
    n: option.every.n,
    unit: option.every.unit,
    shiftRule,
    ...(input.from ? { from: input.from } : {}),
    ...(end.kind === 'date' ? { until: end.date } : {}),
    ...(end.kind === 'times' ? { times: end.times } : {}),
  };
}

/** Какой вариант списка соответствует расписанию — для открытия правки. */
export function repeatOf(schedule: PaymentSchedule | undefined): RepeatKey {
  if (!schedule) return DEFAULT_REPEAT;          // старая запись без расписания
  if (schedule.kind === 'once') return 'never';
  const found = REPEAT_OPTIONS.find(
    (o) => o.every && o.every.n === (schedule.n ?? 1) && o.every.unit === schedule.unit,
  );
  // Ритм, которого нет в списке (например, «каждые 5 месяцев» из более старой
  // версии), не притворяется месячным: форма покажет его как есть.
  return found?.key ?? DEFAULT_REPEAT;
}

/** Чем кончается расписание — для открытия правки. */
export function endOf(schedule: PaymentSchedule | undefined): RepeatEnd {
  if (!schedule) return NEVER_ENDS;
  if (schedule.until) return { kind: 'date', date: schedule.until };
  if (schedule.times !== undefined) return { kind: 'times', times: schedule.times };
  return NEVER_ENDS;
}

/**
 * Правило словами: «ежемесячно до 1 декабря», «каждые 2 недели, 10 платежей».
 *
 * Показывается в списке платежей и под полем: человек должен видеть, что
 * получилось, не открывая форму заново.
 */
export function repeatText(schedule: PaymentSchedule): string {
  const base = scheduleLabel(schedule);
  if (schedule.kind === 'once') return base;
  if (schedule.until) return `${base} до ${human(schedule.until)}`;
  if (schedule.times !== undefined) {
    return `${base}, ${schedule.times} ${plural(schedule.times)}`;
  }
  return base;
}

/** Окончание словами — отдельно, для подписи под полем. */
export function endText(end: RepeatEnd): string {
  if (end.kind === 'date') return `до ${human(end.date)}`;
  if (end.kind === 'times') return `${end.times} ${plural(end.times)}`;
  return 'никогда';
}

const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

const human = (d: PlainDate): string => {
  const [, m, day] = d.split('-');
  return `${Number(day)} ${MONTHS[Number(m) - 1]}`;
};

const plural = (n: number): string => {
  const ten = n % 100;
  if (ten >= 11 && ten <= 14) return 'платежей';
  switch (n % 10) {
    case 1: return 'платёж';
    case 2: case 3: case 4: return 'платежа';
    default: return 'платежей';
  }
};
